import { useBotAnalyticsCRUDActions } from '@mm/state/actions/BotAnalyticsCRUDActions';
import {
  XRayAnalyticsType,
  X_RAY_ANALYTICS_FILTER_TYPES,
} from '@mm/state/interfaces/botAnalytics';
import { selectXRayAnalytics } from '@mm/state/selectors/botAnalyticsSelector';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { Tabs, TabsProps } from 'antd';
import classNames from 'classnames';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { BotBuilderRoutes } from '../../../../../../routes/bot-builder-routes';
import { CAPIConfigurations } from '../../Integrations/NativeIntegrations/components/FacebookAdsIntegrations/CAPIConfigurations';
import { AnalyticsFilterByDate } from '../BotAnalytics/AnalyticsFilterByTime/AnalyticsFilterByDate';
import { XRayContactsLineChart } from './AnalyticsCharts/XRayContactsLineChart';
import { XRayContactsPieChart } from './AnalyticsCharts/XRayContactsPieChart';
import {
  RestoreMetaAnalytics,
  RestoreMetaAnalyticsEmptyState,
} from './RestoreMetaAnalytics/RestoreMetaAnalytics';
import styles from './XRayAnalytics.module.css';
import { XRayAnalyticsAvgSummary } from './XRayAnalyticsAvgSummary/XRayAnalyticsAvgSummary';
import { XRayContactsByLandingPage } from './XRayContactsByLandingPage/XRayContactsByLandingPage';

export const XRayAnalytics = () => {
  const xRayAnalytics = useSelector(selectXRayAnalytics);
  const [startDate, setStartDate] = useState<moment.Moment | null>(
    moment().subtract(30, 'days'),
  );
  const [busy, setBusy] = useState<XRayAnalyticsType[]>([]);
  const [endDate, setEndDate] = useState<moment.Moment | null>(moment());
  const [dateRangeMode, setDateRangeMode] = useState<string | null>(
    'Last 30 days',
  );
  const [contactsCapturedActiveTab, setContactsCapturedActiveTab] =
    useState<XRayAnalyticsType>(X_RAY_ANALYTICS_FILTER_TYPES.wday);
  const [leadScoringActiveTab, setLeadScoringActiveTab] =
    useState<XRayAnalyticsType>(X_RAY_ANALYTICS_FILTER_TYPES.timeOnSite);

  const botId = useSelector(activeChatbotIdSelector);
  const { retrieveXRayAnalytics, resetAllBotAnalytics } =
    useBotAnalyticsCRUDActions();
  const history = useHistory();

  const navigateToSuppressions = () =>
    history.push(BotBuilderRoutes.integrations.getLink({ chatbotId: botId }), {
      kind: 'contact-suppression',
    });

  const pieChartFooter = (
    <div className={styles.pieFooter}>
      A max. of 15 domains are shown
      <div onClick={navigateToSuppressions} className={styles.link}>
        Suppress email providers you do not want
      </div>
    </div>
  );

  const contactsStats: TabsProps['items'] = [
    {
      key: X_RAY_ANALYTICS_FILTER_TYPES.wday,
      label: 'Day of Week',
      children: (
        <XRayContactsPieChart
          type={X_RAY_ANALYTICS_FILTER_TYPES.wday}
          footer={pieChartFooter}
          busy={busy.includes(X_RAY_ANALYTICS_FILTER_TYPES.wday)}
          withLegend
        />
      ),
    },
    {
      key: X_RAY_ANALYTICS_FILTER_TYPES.emailDomain,
      label: 'Email Provider',
      children: (
        <XRayContactsPieChart
          type={X_RAY_ANALYTICS_FILTER_TYPES.emailDomain}
          busy={busy.includes(X_RAY_ANALYTICS_FILTER_TYPES.emailDomain)}
        />
      ),
    },
  ];

  const leadScoringStats: TabsProps['items'] = [
    {
      key: X_RAY_ANALYTICS_FILTER_TYPES.timeOnSite,
      label: 'Time on Site',
      children: (
        <XRayContactsPieChart
          type={X_RAY_ANALYTICS_FILTER_TYPES.timeOnSite}
          busy={busy.includes(X_RAY_ANALYTICS_FILTER_TYPES.timeOnSite)}
          withLegend
        />
      ),
    },
    {
      key: X_RAY_ANALYTICS_FILTER_TYPES.pageViews,
      label: 'Page Views',
      children: (
        <XRayContactsPieChart
          type={X_RAY_ANALYTICS_FILTER_TYPES.pageViews}
          busy={busy.includes(X_RAY_ANALYTICS_FILTER_TYPES.pageViews)}
          withLegend
        />
      ),
    },
    {
      key: X_RAY_ANALYTICS_FILTER_TYPES.clicks,
      label: 'Clicks',
      children: (
        <XRayContactsPieChart
          type={X_RAY_ANALYTICS_FILTER_TYPES.clicks}
          busy={busy.includes(X_RAY_ANALYTICS_FILTER_TYPES.clicks)}
          withLegend
        />
      ),
    },
  ];
  const fetchData = async (type: XRayAnalyticsType, shouldRefetch = true) => {
    const analyticsByType = xRayAnalytics?.[type];
    const formattedStartDate = startDate?.format('YYYY-MM-DD');
    const formattedEndDate = endDate?.format('YYYY-MM-DD');
    const params = {
      botId,
      startDate: formattedStartDate,
      endDate: formattedEndDate,
    };
    if (analyticsByType && !shouldRefetch) return;
    setBusy(state => [...state, type]);
    await retrieveXRayAnalytics(type, params);
    setBusy(state => state.filter(t => t !== type));
  };

  useEffect(() => {
    fetchData(X_RAY_ANALYTICS_FILTER_TYPES.topUrls);
    fetchData(X_RAY_ANALYTICS_FILTER_TYPES.emailSent);
    fetchData(X_RAY_ANALYTICS_FILTER_TYPES.contactsGathered);
    fetchData(X_RAY_ANALYTICS_FILTER_TYPES.avgLeadScores);
    fetchData(X_RAY_ANALYTICS_FILTER_TYPES.webSessionsCount);
    fetchData(contactsCapturedActiveTab);
    fetchData(leadScoringActiveTab);

    return () => {
      resetAllBotAnalytics();
    };
  }, [startDate, endDate]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        X-Ray Pixel Analytics
        <div className={styles.calendarContainer}>
          View by date
          <AnalyticsFilterByDate
            className={styles.calendar}
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            dateRangeMode={dateRangeMode}
            setDateRangeMode={setDateRangeMode}
          />
        </div>
      </div>
      <XRayAnalyticsAvgSummary />
      <XRayContactsLineChart
        busy={
          busy.includes(X_RAY_ANALYTICS_FILTER_TYPES.contactsGathered) ||
          busy.includes(X_RAY_ANALYTICS_FILTER_TYPES.emailSent)
        }
      />
      <div className={styles.gridRow}>
        <div className={classNames(styles.pieContainer, styles.pieChart)}>
          <div className={styles.pieChartHeader}>Contacts Captured</div>
          <Tabs
            activeKey={contactsCapturedActiveTab}
            items={contactsStats}
            rootClassName={styles.chartTabs}
            tabBarGutter={32}
            onChange={(activeKey: XRayAnalyticsType) => {
              setContactsCapturedActiveTab(activeKey);
              fetchData(activeKey, false);
            }}
          />
        </div>
        <div className={classNames(styles.pieContainer, styles.pieChart)}>
          <div className={styles.pieChartHeader}>Customers.ai Lead Scoring</div>
          <Tabs
            activeKey={leadScoringActiveTab}
            items={leadScoringStats}
            rootClassName={styles.chartTabs}
            tabBarGutter={32}
            onChange={(activeKey: XRayAnalyticsType) => {
              setLeadScoringActiveTab(activeKey);
              fetchData(activeKey, false);
            }}
          />
        </div>
        <div className={classNames(styles.pieContainer, styles.pieChart)}>
          <div className={styles.pieChartHeader}>
            Contacts who visited more than once{' '}
          </div>
          <div style={{ height: 64 }} />
          <XRayContactsPieChart
            type={X_RAY_ANALYTICS_FILTER_TYPES.webSessionsCount}
            busy={busy.includes(X_RAY_ANALYTICS_FILTER_TYPES.webSessionsCount)}
            withLegend
          />
        </div>
      </div>
      <div className={styles.chartsRow}>
        <XRayContactsByLandingPage
          busy={busy.includes(X_RAY_ANALYTICS_FILTER_TYPES.topUrls)}
        />
        <div className={styles.metaAnalyticsBlock}>
          <RestoreMetaAnalytics />
          <CAPIConfigurations
            className={styles.capiAnalytics}
            styles={{
              table: styles.table,
              tableHeader: styles.tableHeader,
              tableRows: styles.tableRows,
            }}
            withHeader={false}
            gridColumns="80% 20%"
            remappedColumns={{
              event: (
                <div>
                  Super-CAPI Event Matching{' '}
                  <span className={styles.note}>(All time)</span>
                </div>
              ),
              total_sent: 'Total Events Sent',
            }}
            emptyStateComponent={
              <div
                className={classNames(
                  styles.table,
                  'integrations-grid',
                  styles.emptyTable,
                )}
              >
                <div
                  className={classNames(styles.tableHeader, styles.emptyHeader)}
                >
                  <span>Super-CAPI Event Matching</span>
                  <span>Total Events Sent</span>
                </div>
                <RestoreMetaAnalyticsEmptyState button="Set up Super-CAPI" />
              </div>
            }
          />
        </div>
      </div>
    </div>
  );
};
